import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import * as React from 'react';
import { Root, createRoot } from 'react-dom/client';
import {
  Subject,
  combineLatest, filter, takeUntil
} from 'rxjs';

import { CommonModule } from '@angular/common';
import { ProviderSharedService } from '@sondermind/data-access/micro-shared';

import { Patient } from '@sondermind/utilities/models-patient';

import { ClientDetailPageService } from '@sondermind/features/provider-facing/client-detail';
import { MiniformTemplateStore } from '@sondermind/data-access/miniforms';
import { TreatmentPlanComponent } from '@sondermind/react/features/notes';
import { Logger } from '@sondermind/datadog-logging';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { CHAT_STORE, IChatStore } from '@sondermind/data-access/chat';
import { SideNavDisplayModeService } from '@sondermind/features/provider-facing/side-nav';

const containerElementName = 'treatmentPlanContainer';

@Component({
  selector: 'member-client-detail-treatmentplan',
  template: `<div class="center-children" #${containerElementName}></div>`,
  styleUrls: ['./client-detail-treatmentplan-component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule
  ]
})

export class ClientDetailTreatmentplanComponent implements OnDestroy, AfterViewInit {
  @ViewChild(containerElementName, { static: false }) containerRef!: ElementRef;
  root: Root;
  private patient: Patient;
  private destroyed$ = new Subject<void>();

  constructor(
    private router: Router,
    public route: ActivatedRoute,
    private templateStore: MiniformTemplateStore,
    private clientDetailPageService: ClientDetailPageService,
    private microSharedService: ProviderSharedService,
    private logger: Logger,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
    @Inject(CHAT_STORE)
    private chatService: IChatStore,
    private sideNavService: SideNavDisplayModeService,
  ) {
  }

  ngAfterViewInit(): void {
    this.root = createRoot(this.containerRef.nativeElement);
    try {
      combineLatest([
        this.route.params,
        this.route.queryParams,
        this.clientDetailPageService.patient,
        this.templateStore.noteTemplates,
        this.featureFlagService.isEnabled$(FeatureFlagKey.USE_TREATMENT_PLAN_SHARING),
        this.featureFlagService.isEnabled$(FeatureFlagKey.USE_PUBNUB_SERVICE_MESSAGING)
      ])
        .pipe(
          takeUntil(this.destroyed$),
          filter(([patient]) => patient !== null),
        )
        .subscribe(([params, queryParams, patient, templates, sharingEnabled, usePubnubMessaging]) => {
          if (this.patient !== patient) {
            this.patient = patient;
          }
          if (params.note_id) {
            this.sideNavService.setCompact(true);
          } else {
            this.sideNavService.setCompact(false);
          }
          this.root?.render(
            React.createElement(TreatmentPlanComponent, {
              client: patient,
              sharedService: this.microSharedService,
              templates,
              openMessageDialog: this.openMessageDialog.bind(this),
              sharingEnabled: sharingEnabled ?? false,
              handleGoTo: this.handleGoTo.bind(this),
              forNoteId: queryParams.forNoteId ?? '',
              noteId: params.note_id ?? '',
              handleGoToPlan: this.handleGoToPlan.bind(this),
              usePubnubMessaging: usePubnubMessaging ?? false
            })
          );
        });
    } catch (error) {
      this.logger.error('Error in ngAfterViewInit of ClientDetailTreatmentplanComponent', {
        error: error.message || error,
        component: 'ClientDetailTreatmentplanComponent',
        method: 'ngAfterViewInit'
      });
    }
  }

  openMessageDialog(): void {
    this.chatService.openChatRoomByPlatformIdentity$(this.patient.platformIdentity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  handleGoTo(route: string, params?: Record<string, string | number>): void {
    this.router.navigate([route], { queryParams: params, queryParamsHandling: 'replace' })
      .catch((error) => {
        this.logger.error('Error navigating to route', {
          route,
          params,
          error: error.message || error,
          component: 'ClientDetailTreatmentplanComponent',
          method: 'handleGoTo'
        });
      });
  }

  handleGoToPlan(planId: string, noteId: string): void {
    this.router.navigate(['/client', this.patient.id, 'treatment_plan', planId], {
      queryParams: { forNoteId: noteId },
      queryParamsHandling: 'merge'
    })
      .catch((error) => {
        this.logger.error('Error navigating to treatment plan', {
          planId,
          error: error.message || error,
          component: 'ClientDetailTreatmentplanComponent',
          method: 'handleGoToPlan'
        });
      });
  }

  ngOnDestroy(): void {
    // Unmount React app when angular wrapper is unmounted
    this.root.unmount();
    this.destroyed$.next();
  }
}
